import { useCallback, useEffect, useState } from "react";
import { API_BASE_URL } from "../../../config/api";
import { ButtonLoader, Skeleton } from "../../../Components/AsyncState";

const USD_TO_IRR_RATE_ENDPOINT = `${API_BASE_URL}/api/v0/payment-payout/admin/usd-to-irr-rate/`;

async function authorizedFetch(url, options = {}) {
  const headers = {
    "Content-Type": "application/json",
    ...(options.headers || {}),
    Authorization: `Bearer ${localStorage.getItem("access")}`,
  };

  let response = await fetch(url, {
    ...options,
    headers,
  });

  if (response.status !== 401) {
    return response;
  }

  const refreshResponse = await fetch(
    `${API_BASE_URL}/api/v0/auth/admin/jwt/refresh/`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        refresh: localStorage.getItem("refresh"),
      }),
    },
  );

  if (!refreshResponse.ok) {
    localStorage.removeItem("access");
    localStorage.removeItem("authCredentials");
    localStorage.removeItem("refresh");
    localStorage.removeItem("role");
    localStorage.removeItem("email");
    window.location.href = "/signin";
    return null;
  }

  const refreshData = await refreshResponse.json();
  localStorage.setItem("access", refreshData.access);

  return fetch(url, {
    ...options,
    headers: {
      ...headers,
      Authorization: `Bearer ${localStorage.getItem("access")}`,
    },
  });
}

function formatFlooredRate(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return "-";

  return Math.floor(numericValue).toLocaleString("en-US");
}

function normalizeRatePayload(payload) {
  if (!payload) return null;
  if (payload.rate !== undefined) return payload;
  if (payload.data?.rate !== undefined) return payload.data;
  if (payload.result?.rate !== undefined) return payload.result;
  if (payload.results?.rate !== undefined) return payload.results;
  if (Array.isArray(payload.results) && payload.results[0]?.rate !== undefined) {
    return payload.results[0];
  }
  if (Array.isArray(payload) && payload[0]?.rate !== undefined) {
    return payload[0];
  }

  return null;
}

function formatTaskUpdateDateTime(value, timezone) {
  if (!value) return { time: "-", date: "-" };

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return { time: String(value), date: "-" };
  }

  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-US-u-ca-persian", {
      timeZone: timezone || "Asia/Tehran",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    })
      .formatToParts(date)
      .filter((part) => part.type !== "literal")
      .map((part) => [part.type, part.value]),
  );

  return {
    time: `${parts.hour}:${parts.minute}:${parts.second}`,
    date: `${parts.year}/${parts.month}/${parts.day}`,
  };
}

export default function DollarContainer() {
  const [rateData, setRateData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState("");

  const updateDateTime = formatTaskUpdateDateTime(
    rateData?.next_task_update_at,
    rateData?.next_task_update_timezone,
  );

  const fetchRate = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await authorizedFetch(USD_TO_IRR_RATE_ENDPOINT, {
        method: "GET",
      });

      if (!response) return;
      if (!response.ok) {
        throw new Error(`usd-to-irr-rate-fetch-failed-${response.status}`);
      }

      const payload = await response.json();
      const normalizedPayload = normalizeRatePayload(payload);

      if (!normalizedPayload) {
        throw new Error("usd-to-irr-rate-response-missing-rate");
      }

      setRateData(normalizedPayload);
    } catch (fetchError) {
      console.error("Error fetching USD to IRR rate:", fetchError);
      setError("بارگذاری قیمت دلار با خطا مواجه شد.");
    } finally {
      setLoading(false);
    }
  }, []);

  const updateRate = async () => {
    setUpdating(true);
    setError("");

    try {
      const response = await authorizedFetch(USD_TO_IRR_RATE_ENDPOINT, {
        method: "POST",
        body: JSON.stringify({}),
      });

      if (!response) return;
      if (!response.ok) {
        throw new Error(`usd-to-irr-rate-update-failed-${response.status}`);
      }

      const payload = await response.json();
      const normalizedPayload = normalizeRatePayload(payload);

      if (!normalizedPayload) {
        throw new Error("usd-to-irr-rate-response-missing-rate");
      }

      setRateData(normalizedPayload);
    } catch (updateError) {
      console.error("Error updating USD to IRR rate:", updateError);
      setError("به‌روزرسانی قیمت دلار با خطا مواجه شد.");
    } finally {
      setUpdating(false);
    }
  };

  useEffect(() => {
    fetchRate();
  }, [fetchRate]);

  return (
    <section className="w-full max-w-sm rounded-3xl p-6 flex flex-col gap-6 bg-zi-bg2-light dark:bg-zi-bg2-dark text-zi-text-light dark:text-zi-text-dark shadow-2xl">
      <div className="w-full flex items-center justify-between gap-5">
        <span className="opacity-50">قیمت فعلی دلار به ریال:</span>
        <span className="font-medium">
          {loading ? <Skeleton className="h-5 w-24" /> : formatFlooredRate(rateData?.rate)}
        </span>
      </div>
      <div className="w-full flex items-center justify-between gap-5">
        <span className="opacity-50">به روز رسانی در:</span>
        <div className="flex gap-2.5 font-medium">
          <div>{loading ? <Skeleton className="h-5 w-16" /> : updateDateTime.time}</div>
          <div>{loading ? <Skeleton className="h-5 w-20" /> : updateDateTime.date}</div>
        </div>
      </div>
      {error ? <p className="text-sm text-red-500">{error}</p> : null}
      <button
        type="button"
        onClick={updateRate}
        disabled={loading || updating}
        className="w-full rounded-full bg-zi-gradient-button-blue text-zi-text-dark flex items-center justify-center py-2.5 self-center outline-none hocus:ring-offset-1 hocus:ring-1 mt-6 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {updating ? <ButtonLoader label="در حال به‌روزرسانی قیمت" /> : "همین حالا قیمت را به روز رسانی کن"}
      </button>
    </section>
  );
}
